import { useCallback, useRef, useState } from "react";
import { DataTableColumn } from "../types";

export const FROZEN_SELECT_ID = "__select__";
export const FROZEN_EXPAND_ID = "__expand__";

export interface FrozenCellInfo {
  side: "left" | "right";
  offset: number;
  /** true for the last frozen cell on its side, used to draw the divider shadow */
  edge: boolean;
}

export interface FrozenColumnsState {
  /** Attach to the header cell (th) so its rendered width can be tracked. */
  registerCell: (id: string, el: HTMLElement | null) => void;
  /** Stable per-id ref callback for `registerCell`, so `<th ref={...}>` doesn't detach/reattach on every render. */
  getCellRef: (id: string) => (el: HTMLElement | null) => void;
  getFreezeInfo: (id: string) => FrozenCellInfo | undefined;
  isSelectFrozen: boolean;
  isExpandFrozen: boolean;
  /** Total width reserved by the left/right frozen chains, for clamping sticky header labels in the scrollable middle columns. */
  leftReservedWidth: number;
  rightReservedWidth: number;
}

function isFrozen<TRow>(column: DataTableColumn<TRow>) {
  return !!column.freeze;
}

/**
 * Resolves `freeze` into sticky offsets. The side is derived from position: a
 * contiguous run of frozen columns starting at index 0 sticks left, and a
 * contiguous run ending at the last index sticks right — freezing in the middle
 * is not supported. A fully frozen column set resolves entirely to the left.
 * Widths are measured from the header cells (table layout keeps body cells the
 * same width as their header), so offsets stay correct as columns resize.
 */
export function useFrozenColumns<TRow>(
  columns: DataTableColumn<TRow>[],
  hasSelectionColumn: boolean,
  hasExpansionColumn: boolean,
): FrozenColumnsState {
  const elementsRef = useRef(new Map<string, HTMLElement>());
  const [widths, setWidths] = useState<Record<string, number>>({});

  const observerRef = useRef<ResizeObserver | null>(null);
  const getObserver = () => {
    if (!observerRef.current && typeof ResizeObserver !== "undefined") {
      observerRef.current = new ResizeObserver((entries) => {
        setWidths((prev) => {
          let changed = false;
          const next = { ...prev };
          for (const entry of entries) {
            const id = (entry.target as HTMLElement).dataset.frozenId;
            if (!id) continue;
            // Rounded: sub-pixel jitter from fractional OS display scaling can make
            // the same visual width read back slightly differently between layout
            // passes, which would otherwise defeat the equality check below and
            // spin the observer/setState loop indefinitely.
            const width = Math.round(
              entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width,
            );
            if (next[id] !== width) {
              next[id] = width;
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      });
    }
    return observerRef.current;
  };

  const registerCell = useCallback((id: string, el: HTMLElement | null) => {
    const map = elementsRef.current;
    const prevEl = map.get(id);
    if (prevEl === el) return;

    const observer = getObserver();
    if (prevEl && observer) observer.unobserve(prevEl);

    if (el) {
      el.dataset.frozenId = id;
      map.set(id, el);
      observer?.observe(el);
      const width = Math.round(el.getBoundingClientRect().width);
      setWidths((prev) => (prev[id] === width ? prev : { ...prev, [id]: width }));
    } else {
      map.delete(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // registerCell's identity is stable, but consumers previously passed inline
  // `(el) => frozen.registerCell(id, el)}` arrows as `ref` props — a new function
  // every render, which makes React detach/reattach the ref (and re-measure) on
  // every re-render instead of only on mount/unmount. Cache one bound callback
  // per column id so `ref={frozen.getCellRef(id)}` stays referentially stable.
  const cellRefsRef = useRef(new Map<string, (el: HTMLElement | null) => void>());
  const getCellRef = useCallback(
    (id: string) => {
      const cache = cellRefsRef.current;
      let cb = cache.get(id);
      if (!cb) {
        cb = (el: HTMLElement | null) => registerCell(id, el);
        cache.set(id, cb);
      }
      return cb;
    },
    [registerCell],
  );

  let leftEnd = 0;
  while (leftEnd < columns.length && isFrozen(columns[leftEnd])) leftEnd++;

  let rightStart = columns.length;
  while (rightStart > leftEnd && isFrozen(columns[rightStart - 1])) rightStart--;

  const leftIds = columns.slice(0, leftEnd).map((c) => c.id);
  const rightIds = columns.slice(rightStart).map((c) => c.id);

  const isSelectFrozen = leftIds.length > 0 && hasSelectionColumn;
  const isExpandFrozen = rightIds.length > 0 && hasExpansionColumn;

  const leftOrder = isSelectFrozen ? [FROZEN_SELECT_ID, ...leftIds] : leftIds;
  const rightOrder = isExpandFrozen ? [...rightIds, FROZEN_EXPAND_ID] : rightIds;

  const info = new Map<string, FrozenCellInfo>();

  let acc = 0;
  leftOrder.forEach((id, i) => {
    info.set(id, { side: "left", offset: acc, edge: i === leftOrder.length - 1 });
    acc += widths[id] ?? 0;
  });
  const leftReservedWidth = acc;

  acc = 0;
  for (let i = rightOrder.length - 1; i >= 0; i--) {
    const id = rightOrder[i];
    info.set(id, { side: "right", offset: acc, edge: i === 0 });
    acc += widths[id] ?? 0;
  }
  const rightReservedWidth = acc;

  const getFreezeInfo = useCallback(
    (id: string) => info.get(id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columns, widths, hasSelectionColumn, hasExpansionColumn],
  );

  return {
    registerCell,
    getCellRef,
    getFreezeInfo,
    isSelectFrozen,
    isExpandFrozen,
    leftReservedWidth,
    rightReservedWidth,
  };
}
